import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Route,
  CheckCircle2,
  AlertTriangle,
  Check,
  Clock,
  Phone,
  Wallet,
  MapPin,
  HelpCircle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import {
  useSeniorFlowLanding,
  useAcknowledgeOutreach,
  useAcceptInterview,
  useDeclineInterview,
} from 'src/hooks/useSeniorFlow';
import { IntroStage } from './IntroStage';
import { DetailsStage } from './DetailsStage';
import {
  deriveStage,
  landingFields,
  acknowledgedLabel,
  fmtDate,
} from './seniorFlowFields';
import './SeniorLevelFlow.css';

const DEMO_LANDING = {
  candidate_name: 'Dinesh Hegde',
  organization: 'Visa',
  job_title: 'Compliance Manager',
  minimum_salary: null,
  maximum_salary: 2000000,
  min_work_experience: 60,
  max_work_experience: null,
  location_id: 5,
  location_name: 'Bengaluru',
  employment_type: 'Full-Time',
  work_mode: null,
  current_status: 'Contacted',
  acknowledged_at: null,
  interview_datetime: null,
  detailed_job_description:
    "## About the Role\n\nAs a Compliance Manager, you will be at the forefront of establishing and maintaining compliance frameworks that align with regulatory standards. Your role is crucial in safeguarding the organization's integrity and reputation by ensuring adherence to laws and regulations. You will work closely with senior management, regulators, and various business units to implement robust compliance policies and procedures.\n\n## Key Responsibilities\n\n- Design and implement compliance policies and procedures to meet regulatory requirements.\n- Conduct regular risk assessments and compliance audits to identify and mitigate risks.\n- Advise senior management on regulatory changes and their impact on business operations.\n- Monitor and report compliance performance to internal stakeholders and regulatory bodies.\n- Develop and deliver compliance training programs to enhance awareness across the organization.\n- Partner with internal audit and risk management teams to ensure cohesive risk mitigation strategies.\n- Oversee investigations into compliance breaches and recommend corrective actions.\n\n## Required Qualifications\n\n**Education:** Bachelor's degree in Business / Finance\n\n**Experience:** 5+ years in compliance or related fields\n\n**Core Skills:** Regulatory Compliance, Risk Management, Policy Development\n\n**Certifications:** Certified Anti-Money Laundering Specialist (CAMS)\n\n**Degree Requirements:**\n\n  - **Primary degree** (required): Bachelor's degree in Business / Finance\n\n## Preferred Qualifications\n\n- Experience in financial services compliance\n- Knowledge of international regulatory frameworks\n- Certified Information Systems Auditor (CISA)\n- Exposure to emerging compliance technologies\n\n## Skills\n\n**Technical Skills:** Regulatory Compliance (Advanced, 5+ years), Risk Management, Policy Development, Compliance Audits\n\n**Soft Skills:** Analytical Thinking, Communication, Attention to Detail, Problem Solving\n\n## Role Profile\n\n**Career Level:** Mid Level\n\n**Domain(s):** Regulatory Compliance (primary), Risk Management\n\n**Industry:** Not specified\n\n**Company Size:** Not specified\n\n**Team Leadership:** Yes\n\n## Logistics\n\n**Work Location(s):** Not specified\n\n**Languages:** Not specified\n\n**Compensation:** Not specified\n\n**Target Joining Date:** Not specified\n\n**Application Closing Date:** Not specified",
};

// Preview the placeholder UI locally when there's no token; never in production.
const isDev = process.env.NODE_ENV !== 'production';

function StepTracker({ stage, acknowledged }) {
  if (!['intro', 'details', 'thanks'].includes(stage)) return null;
  let s1 = 'now';
  let s2 = '';
  let line = '';
  if (stage === 'intro') {
    s1 = acknowledged ? 'done' : 'now';
    line = acknowledged ? 'done' : '';
  } else if (stage === 'details') {
    s1 = 'done';
    s2 = 'now';
    line = 'done';
  } else {
    s1 = 'done';
    s2 = 'done';
    line = 'done';
  }
  return (
    <ol className="slf-tracker" aria-label="Progress">
      <li className="slf-tk" data-state={s1 || undefined}>
        <span className="slf-tk-dot" aria-hidden="true">
          1
        </span>
        <span className="slf-tk-label">Your interest</span>
      </li>
      <span
        className="slf-tk-line"
        data-state={line || undefined}
        aria-hidden="true"
      />
      <li className="slf-tk" data-state={s2 || undefined}>
        <span className="slf-tk-dot" aria-hidden="true">
          2
        </span>
        <span className="slf-tk-label">A few details</span>
      </li>
    </ol>
  );
}

function LoadingSkeleton() {
  return (
    <section className="slf-stage-in" aria-label="Loading">
      <div
        className="slf-skel slf-skel-line"
        style={{ width: '38%', height: 10 }}
      />
      <div
        className="slf-skel slf-skel-line"
        style={{ width: '72%', height: 20, marginTop: 14 }}
      />
      <div
        className="slf-skel slf-skel-line"
        style={{ width: '52%', height: 11 }}
      />
      <div className="slf-skel slf-skel-block" />
      <div className="slf-skel slf-skel-block" style={{ height: 150 }} />
    </section>
  );
}

function ThanksStage({ firstName, companyName, values }) {
  const lpa = (n) => `₹${Number.isInteger(n) ? n : n.toFixed(1)} LPA`;
  const rows = [
    values.currentCtc != null && [
      <Wallet key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Current fixed CTC',
      <span className="slf-mono">{lpa(values.currentCtc)}</span>,
    ],
    values.expectedCtc != null && [
      <Wallet key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Expected fixed CTC',
      <span className="slf-mono">{lpa(values.expectedCtc)}</span>,
    ],
    values.notice != null && [
      <Clock key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Notice period',
      values.notice === 0 ? (
        'Immediate'
      ) : (
        <>
          <span className="slf-mono">{values.notice}</span> days
        </>
      ),
    ],
    values.locations?.length && [
      <MapPin key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Preferred locations',
      `${values.locations.join(', ')}${
        values.relocate ? ' · open to relocating' : ''
      }`,
    ],
    values.phone && [
      <Phone key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Contact number',
      <span className="slf-mono">+91 {values.phone}</span>,
    ],
    values.note && [
      <HelpCircle key="i" className="slf-fact-ic" aria-hidden="true" />,
      'Your note',
      values.note,
    ],
  ].filter(Boolean);

  return (
    <section className="slf-stage-in" aria-labelledby="slf-thanksHead">
      <div className="slf-success">
        <span className="slf-success-ic" aria-hidden="true">
          <Check />
        </span>
        <h2 id="slf-thanksHead">Details sent</h2>
        <p className="slf-read">
          Thanks, {firstName} — everything we need is with the Ahedge team. You
          don&apos;t need to do anything else.
        </p>
      </div>

      <h3 style={{ margin: '24px 0 8px' }}>What you sent us</h3>
      <div className="slf-facts">
        {rows.map(([icon, key, val]) => (
          <div className="slf-fact" key={key}>
            {icon}
            <div>
              <span className="slf-fact-k">{key}</span>
              <span className="slf-sum-v">{val}</span>
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ margin: '24px 0 8px' }}>What happens next</h3>
      <ol className="slf-steps" style={{ marginTop: 14 }}>
        <li>
          <b>We read through your details</b>
          <span>
            Today or tomorrow. If something doesn&apos;t line up, we&apos;ll
            tell you straight away rather than leave you waiting.
          </span>
        </li>
        <li>
          <b>A recruiter calls you</b>
          <span>
            Within two working days, to walk through the role and answer your
            questions.
          </span>
        </li>
        <li>
          <b>You decide whether to go forward</b>
          <span>
            Only then does your profile go to {companyName}. Not before.
          </span>
        </li>
      </ol>
    </section>
  );
}

function TerminalPanel({ eyebrow, title, children }) {
  return (
    <section className="slf-stage-in">
      {eyebrow ? <p className="slf-eyebrow">{eyebrow}</p> : null}
      <h2 style={{ marginTop: 10 }}>{title}</h2>
      {children}
    </section>
  );
}

export default function SeniorLevelFlow() {
  // Reached either via a magic link (`?token=`) or, authenticated, from the
  // "My Applications" list (`/my-applications/requests/:secMappingId`) — the
  // latter carries no token and hits the protected mirror endpoints instead.
  const { secMappingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isProtectedRoute = !!secMappingId;
  const scope = isProtectedRoute ? { secMappingId } : { token };
  const { toast } = useToast();

  const demo = !token && !isProtectedRoute && isDev;

  const { data: landingData, isLoading, isError } = useSeniorFlowLanding(scope);
  const data = demo ? DEMO_LANDING : landingData;

  const acknowledge = useAcknowledgeOutreach(scope);
  // The interview embedded in this same flow (the `interview`/`interviewDone`
  // stages below) has no protected mirror yet, so this stays token-only — it
  // simply won't fire in protected mode, matching the protected landing
  // payload never populating interview_datetime.
  const acceptInterview = useAcceptInterview(token);
  const declineInterview = useDeclineInterview(token);

  // Local forward-progress overrides (the server is still the source of truth on
  // reload, but these give immediate feedback and carry the submitted receipt).
  const [override, setOverride] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [interviewResult, setInterviewResult] = useState(null);

  const fields = useMemo(() => landingFields(data || {}), [data]);
  const derived = useMemo(() => deriveStage(data), [data]);

  // Protected mode only: push "My Applications > <role>" up to the app
  // header (same mechanism OpeningDetails.jsx uses), since this view no
  // longer owns the whole page the way the magic-link version does.
  const outletContext = useOutletContext();
  useEffect(() => {
    if (!isProtectedRoute || !outletContext?.setHeaderBreadcrumbs) {
      return undefined;
    }
    outletContext.setHeaderBreadcrumbs([
      { label: 'My Applications', href: '/my-applications' },
      { label: fields.jobTitle || 'Role details' },
    ]);
    return () => outletContext.setHeaderBreadcrumbs?.(null);
  }, [isProtectedRoute, fields.jobTitle, outletContext]);

  const acknowledged =
    override != null ||
    (data ? derived !== 'intro' : false) ||
    !!data?.acknowledged_at;

  const onError = () =>
    toast({
      message: "We couldn't save that just now. Please try again.",
      variant: 'destructive',
    });

  const handleYes = () => {
    if (demo) {
      setOverride('details');
      return;
    }
    acknowledge.mutate(true, {
      onSuccess: () => setOverride('details'),
      onError,
    });
  };
  const handleNo = () => {
    if (demo) {
      setOverride('declined');
      return;
    }
    acknowledge.mutate(false, {
      onSuccess: () => setOverride('declined'),
      onError,
    });
  };
  const handleAccept = () => {
    if (demo) {
      setInterviewResult('accepted');
      setOverride('interviewDone');
      return;
    }
    acceptInterview.mutate(undefined, {
      onSuccess: () => {
        setInterviewResult('accepted');
        setOverride('interviewDone');
      },
      onError,
    });
  };
  const handleDecline = () => {
    if (demo) {
      setInterviewResult('declined');
      setOverride('interviewDone');
      return;
    }
    declineInterview.mutate(undefined, {
      onSuccess: () => {
        setInterviewResult('declined');
        setOverride('interviewDone');
      },
      onError,
    });
  };

  // Resolve the stage to show.
  let stage;
  if (!token && !isProtectedRoute && !demo) stage = 'expired';
  else if (isError) stage = isProtectedRoute ? 'notFound' : 'expired';
  else if (isLoading && !demo) stage = 'loading';
  else stage = override || derived;

  let body;
  if (stage === 'loading') {
    body = <LoadingSkeleton />;
  } else if (stage === 'expired') {
    body = (
      <TerminalPanel title="This link is no longer valid">
        <div className="slf-banner slf-warn" style={{ marginTop: 8 }}>
          <AlertTriangle className="slf-ic" aria-hidden="true" />
          <div>
            <strong>
              Links expire once a role closes, or after a set time.
            </strong>
            Reply to the message we sent you, or write to{' '}
            <a href="mailto:hello@ahedge.com">hello@ahedge.com</a> for a fresh
            link.
          </div>
        </div>
      </TerminalPanel>
    );
  } else if (stage === 'notFound') {
    body = (
      <TerminalPanel title="This role isn't in your pipeline">
        <div className="slf-banner slf-warn" style={{ marginTop: 8 }}>
          <AlertTriangle className="slf-ic" aria-hidden="true" />
          <div>
            <strong>It may have closed, or moved on.</strong>
            <Link to="/my-applications">Back to My Applications</Link>
          </div>
        </div>
      </TerminalPanel>
    );
  } else if (stage === 'details') {
    body = (
      <DetailsStage
        scope={scope}
        firstName={fields.firstName}
        companyName={fields.companyName}
        onSubmitted={(values) => {
          setSubmitted(values);
          setOverride('thanks');
        }}
        onBack={() => setOverride('intro')}
      />
    );
  } else if (stage === 'thanks' && submitted) {
    body = (
      <ThanksStage
        firstName={fields.firstName}
        companyName={fields.companyName}
        values={submitted}
      />
    );
  } else if (stage === 'interview') {
    const f = data?.interview_datetime
      ? fmtDate(data.interview_datetime)
      : null;
    body = (
      <section className="slf-stage-in" aria-labelledby="slf-mtgHead">
        <p className="slf-eyebrow">Interview scheduled</p>
        <h2 id="slf-mtgHead" style={{ marginTop: 10 }}>
          Can you make this, {fields.firstName}?
        </h2>
        <p className="slf-read" style={{ marginTop: 8 }}>
          Your first round with {fields.companyName} is booked. Confirm so they
          know to expect you.
        </p>
        <div className="slf-meet" style={{ marginTop: 18 }}>
          <p className="slf-fact-k">When</p>
          <p className="slf-meet-when">{f ? f.when : '—'}</p>
          {f?.tz ? <p className="slf-meet-tz">{f.tz}</p> : null}
          <div className="slf-actions" style={{ marginTop: 16 }}>
            <button
              className="slf-btn slf-btn-primary"
              type="button"
              onClick={handleAccept}
              disabled={acceptInterview.isPending || declineInterview.isPending}
            >
              <Check aria-hidden="true" />
              {acceptInterview.isPending ? 'Confirming…' : "Yes, I'll be there"}
            </button>
            <button
              className="slf-btn slf-btn-secondary"
              type="button"
              onClick={handleDecline}
              disabled={acceptInterview.isPending || declineInterview.isPending}
            >
              I can&apos;t make this
            </button>
          </div>
        </div>
      </section>
    );
  } else if (stage === 'interviewDone') {
    const f = data?.interview_datetime
      ? fmtDate(data.interview_datetime)
      : null;
    const declined = interviewResult === 'declined';
    body = (
      <section className="slf-stage-in" aria-labelledby="slf-mtgDoneHead">
        <div className={`slf-banner ${declined ? 'slf-warn' : 'slf-ok'}`}>
          {declined ? (
            <AlertTriangle className="slf-ic" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="slf-ic" aria-hidden="true" />
          )}
          <div>
            {declined ? (
              <>
                <strong>Slot declined.</strong>We&apos;ll ask them for another
                time and message you.
              </>
            ) : (
              <>
                <strong>You&apos;re confirmed.</strong>We&apos;ve let the team
                know to expect you.
              </>
            )}
          </div>
        </div>
        <h2 id="slf-mtgDoneHead" style={{ marginTop: 22 }}>
          Your interview
        </h2>
        <div className="slf-meet" style={{ marginTop: 14 }}>
          <p className="slf-fact-k">When</p>
          <p className="slf-meet-when">{f ? f.when : '—'}</p>
          {f?.tz ? <p className="slf-meet-tz">{f.tz}</p> : null}
          {!declined && data?.join_link ? (
            <div className="slf-actions" style={{ marginTop: 16 }}>
              <a
                className="slf-btn slf-btn-primary"
                href={data.join_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Join the call
              </a>
            </div>
          ) : null}
        </div>
      </section>
    );
  } else if (stage === 'declined' || stage === 'closed') {
    body = <DeclinedStage />;
  } else if (stage === 'status') {
    body = (
      <section className="slf-stage-in" aria-labelledby="slf-statusHead">
        <div className="slf-ack-note">
          <CheckCircle2 className="slf-ic" aria-hidden="true" />
          <div>
            <strong>We have your details.</strong>Nothing more is needed from
            you right now.
          </div>
        </div>
        <h2 id="slf-statusHead" style={{ marginTop: 22 }}>
          Where things stand, {fields.firstName}
        </h2>
        <p className="slf-read" style={{ marginTop: 8 }}>
          You&apos;re in the process for <strong>{fields.jobTitle}</strong> at{' '}
          {fields.companyName}. We&apos;ll be in touch as it moves.
        </p>
        <div className="slf-facts" style={{ marginTop: 18 }}>
          <div className="slf-fact">
            <Route className="slf-fact-ic" aria-hidden="true" />
            <div>
              <span className="slf-fact-k">Current stage</span>
              <span className="slf-fact-v">
                {data?.current_status || 'With the Ahedge team'}
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  } else {
    // intro
    body = (
      <IntroStage
        fields={fields}
        acknowledged={acknowledged}
        ackLabel={acknowledgedLabel(data)}
        onYes={handleYes}
        onNo={handleNo}
        onContinue={() => setOverride('details')}
        yesPending={acknowledge.isPending}
        noPending={acknowledge.isPending}
        floatingBar={!isProtectedRoute}
      />
    );
  }

  return (
    <div className="slf-root">
      <main className="slf-wrap">
        <div className="slf-card">
          <StepTracker stage={stage} acknowledged={acknowledged} />
          <div className="slf-card-pad">{body}</div>
        </div>
        <footer className="slf-foot">
          <span>Ahedge · Sent because your profile matched an open role</span>
          <span>
            <a href="/privacy">Privacy</a> ·{' '}
            <a href="/privacy">Don&apos;t contact me again</a>
          </span>
        </footer>
      </main>
    </div>
  );
}

// Decline stage with optional feedback + opt-out (local only until wired to an
// endpoint).
function DeclinedStage() {
  const [done, setDone] = useState(false);
  const [optedOut, setOptedOut] = useState(false);

  const REASONS = [
    ['happy', 'Happy where I am'],
    ['pay', "Pay isn't right"],
    ['location', "Location doesn't work"],
    ['role', 'Wrong role for me'],
    ['new', 'Just started somewhere new'],
  ];

  return (
    <section className="slf-stage-in" aria-labelledby="slf-declHead">
      <p className="slf-eyebrow">Noted</p>
      <h2 id="slf-declHead" style={{ marginTop: 10 }}>
        Understood — thanks for telling us.
      </h2>
      <p className="slf-read" style={{ marginTop: 8 }}>
        We won&apos;t take this one further, and you won&apos;t hear from us
        about this role again.
      </p>

      {done ? (
        <div style={{ marginTop: 20 }}>
          <div className="slf-banner slf-ok">
            <CheckCircle2 className="slf-ic" aria-hidden="true" />
            <div>
              {optedOut ? (
                <>
                  <strong>
                    Done — you&apos;ve been removed from our list.
                  </strong>
                  You won&apos;t hear from us again.
                </>
              ) : (
                <>
                  <strong>Thanks — that&apos;s logged.</strong>Good luck with
                  what&apos;s next.
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <hr className="slf-rule" style={{ margin: '20px 0' }} />
          <div className="slf-field">
            <span className="slf-field-label" id="slf-lblWhy">
              If you don&apos;t mind — what made it a no?{' '}
              <span className="slf-opt">— optional</span>
            </span>
            <span className="slf-field-hint">
              One tap. It genuinely changes which roles we send you.
            </span>
            <div
              className="slf-chips"
              role="radiogroup"
              aria-labelledby="slf-lblWhy"
            >
              {REASONS.map(([val, label]) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label className="slf-chip" key={val}>
                  <input type="radio" name="slf-why" value={val} />
                  <span className="slf-chip-face">
                    <Check className="slf-tick" aria-hidden="true" />
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="slf-chk-row" style={{ marginTop: 16 }}>
            <input type="checkbox" className="slf-chk" />
            <span>Keep me in mind for future roles that fit better</span>
          </label>
          <div className="slf-actions" style={{ marginTop: 18 }}>
            <button
              className="slf-btn slf-btn-primary"
              type="button"
              onClick={() => setDone(true)}
            >
              Send this
            </button>
          </div>
          <p style={{ textAlign: 'center', marginTop: 4 }}>
            <button
              className="slf-btn-link"
              type="button"
              onClick={() => {
                setOptedOut(true);
                setDone(true);
              }}
            >
              Remove me from Ahedge entirely
            </button>
          </p>
        </div>
      )}
    </section>
  );
}
